import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import BaseLayout from "@/v2/components/BaseLayout";
import Attribution from "@/v2/components/Attribution";
import Transparency from "@/v2/components/Transparency";
import RelatedArticles from "@/v2/components/RelatedArticles";
import { fetchArticleBySlug } from "@/v2/queries";
import { isV2Category, labelFor } from "@/v2/categories";
import { decodeEntities, stripHtml } from "@/v2/textUtils";
import { amneLabel, FAKTAKARAKTAR_LABEL } from "@/v2/taxonomy";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [copied, setCopied] = useState(false);

  if (!isV2Category(category) || !slug) {
    return <NotFound />;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ["v2", "article", category, slug],
    queryFn: () => fetchArticleBySlug(category, slug),
  });

  const article = data;

  if (!isLoading && !article) {
    return <NotFound />;
  }

  const title = decodeEntities(article?.title) || "Laddar…";
  const description = decodeEntities(
    stripHtml(article?.aiSummary ?? article?.excerpt ?? "").slice(0, 155),
  );
  const primaryUrl = article?.sources?.[0]?.url ?? article?.trendUrl ?? undefined;

  const decodedSummary = decodeEntities(stripHtml(article?.aiSummary));
  const decodedExcerpt = decodeEntities(stripHtml(article?.excerpt));

  const canonicalUrl = `https://korthet.se/${category}/${slug}`;
  const jsonLd = article
    ? [
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: decodeEntities(article.title),
          description,
          datePublished: article.publishedAt ?? undefined,
          dateModified: article.updatedAt ?? article.publishedAt ?? undefined,
          inLanguage: "sv-SE",
          articleSection: labelFor(article.category),
          mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
          url: canonicalUrl,
          image: ["https://korthet.se/og-image.png"],
          author: {
            "@type": "Organization",
            name: "Korthet-redaktionen",
            url: "https://korthet.se/om-korthet/redaktionen",
          },
          sourceOrganization: article.source
            ? { "@type": "Organization", name: article.source }
            : undefined,
          publisher: {
            "@type": "Organization",
            name: "Korthet",
            url: "https://korthet.se/",
            logo: {
              "@type": "ImageObject",
              url: "https://korthet.se/og-image.png",
            },
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Start",
              item: "https://korthet.se/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: labelFor(article.category),
              item: `https://korthet.se/${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: decodeEntities(article.title),
              item: canonicalUrl,
            },
          ],
        },
      ]
    : undefined;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <BaseLayout
      title={`${title} – Korthet`}
      description={description}
      canonicalPath={`/${category}/${slug}`}
      ogType="article"
      jsonLd={jsonLd}
    >
      {isLoading && <p className="k2-loading">Laddar…</p>}
      {error && <p className="k2-error">Kunde inte hämta artikeln.</p>}
      {article && (
        <article className="k2-article-body">
          <div className="k2-article-meta-row">
            <span className="k2-cat-badge" data-cat={article.category}>
              {labelFor(article.category)}
            </span>
            {article.faktakaraktar && (
              <span
                className="k2-fakt-badge"
                data-fakt={article.faktakaraktar}
                title="Artikeltyp"
              >
                {FAKTAKARAKTAR_LABEL[article.faktakaraktar]}
              </span>
            )}
          </div>
          <h1 className="k2-article-title">{decodeEntities(article.title)}</h1>
          <Attribution article={article} />
          <Transparency
            source={article.source}
            sourceUrl={primaryUrl}
            publishedAt={article.publishedAt}
            updatedAt={article.updatedAt}
          />

          {primaryUrl && (
            <a
              className="k2-source-cta"
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              Läs originalartikeln hos {article.source || "källan"} →
            </a>
          )}

          <div className="k2-actions">
            <button type="button" className="k2-btn" onClick={handleCopy}>
              {copied ? "Kopierad ✓" : "Kopiera länk"}
            </button>
          </div>


          {(decodedSummary || decodedExcerpt) && (
            <section aria-labelledby="summary-heading">
              <h2 id="summary-heading" className="k2-h2">
                Nyheten i korthet
              </h2>
              <p className="k2-summary">
                {decodedSummary || decodedExcerpt}
              </p>
            </section>
          )}

          {article.aiBullets && article.aiBullets.length > 0 && (
            <section aria-labelledby="bullets-heading">
              <h2 id="bullets-heading" className="k2-h2">
                Viktiga punkter
              </h2>
              <ul className="k2-bullets">
                {article.aiBullets.map((b, i) => (
                  <li key={i}>{decodeEntities(b)}</li>
                ))}
              </ul>
            </section>
          )}

          {(article.huvudamne ||
            (article.berordaAmnen && article.berordaAmnen.length > 0) ||
            (article.perspektiv && article.perspektiv.length > 0)) && (
            <section aria-labelledby="profile-heading" className="k2-profile">
              <h2 id="profile-heading" className="k2-h2">
                Artikelprofil
              </h2>
              {article.huvudamne && (
                <p className="k2-profile-row">
                  <span className="k2-profile-label">Huvudämne:</span>{" "}
                  <span className="k2-chip k2-chip-primary">
                    {amneLabel(article.huvudamne)}
                  </span>
                </p>
              )}
              {article.berordaAmnen && article.berordaAmnen.length > 0 && (
                <p className="k2-profile-row">
                  <span className="k2-profile-label">Berörda ämnen:</span>{" "}
                  {article.berordaAmnen.map((s) => (
                    <Link key={s} to={`/amne/${s}`} className="k2-chip">
                      {amneLabel(s)}
                    </Link>
                  ))}
                </p>
              )}
              {article.perspektiv && article.perspektiv.length > 0 && (
                <p className="k2-profile-row">
                  <span className="k2-profile-label">Perspektiv:</span>{" "}
                  {article.perspektiv.map((s) => (
                    <span key={s} className="k2-chip k2-chip-persp">
                      {amneLabel(s)}
                    </span>
                  ))}
                </p>
              )}
            </section>
          )}

          <section className="k2-why" aria-labelledby="why-heading">
            <h2 id="why-heading" className="k2-h2">
              Varför Korthet?
            </h2>
            <p>
              Korthet ersätter inte originaljournalistiken. Vi sammanfattar rapporteringen
              så att du snabbt förstår vad som hänt – och kan gå vidare till originalkällan
              för hela bilden.
            </p>
            {primaryUrl && (
              <a
                className="k2-source-cta"
                href={primaryUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                Läs originalartikeln hos {article.source || "källan"} →
              </a>
            )}
          </section>

          <p className="k2-disclaimer">
            AI-genererad sammanfattning. Originalinnehållet tillhör respektive publikation.
          </p>


          <RelatedArticles category={article.category as any} currentId={article.id} />
        </article>
      )}
    </BaseLayout>
  );
}